import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type {
  Channel,
  Group,
  DirectConversation,
  Message,
  DirectMessage,
  WebSocketEvent
} from '@sahaj/types';
import { generateClientMessageId } from '@sahaj/utils';
import { topics } from '@sahaj/websocket';
import { api, ws } from '../lib/api';
import { useWorkspace } from './WorkspaceContext';
import { useAuth } from './AuthContext';

type ActiveChat = { type: 'channel'; channel: Channel } | { type: 'dm'; conversation: DirectConversation } | null;

interface ChatContextType {
  groups: Group[];
  channels: Channel[];
  directConversations: DirectConversation[];
  activeChat: ActiveChat;
  messages: (Message | DirectMessage)[];
  activeThread: Message | null;
  threadReplies: Message[];
  isLoadingMessages: boolean;
  hasMoreMessages: boolean;
  selectChannel: (channel: Channel) => void;
  selectDm: (conversation: DirectConversation) => void;
  openThread: (message: Message) => void;
  closeThread: () => void;
  sendMessage: (content: string) => Promise<void>;
  sendReply: (content: string) => Promise<void>;
  editMessage: (messageId: string, newContent: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  addReaction: (messageId: string, emoji: string) => Promise<void>;
  removeReaction: (messageId: string, emoji: string) => Promise<void>;
  createChannel: (groupId: string, name: string, description?: string, isPrivate?: boolean) => Promise<Channel>;
  createGroup: (name: string, description?: string) => Promise<Group>;
  deleteChannel: (channelId: string) => Promise<void>;
  deleteGroup: (groupId: string) => Promise<void>;
  createDm: (participantOrReq: string | { participantId?: string; participantIds?: string[]; title?: string }) => Promise<DirectConversation>;
  loadOlderMessages: () => Promise<void>;
  refreshChannels: () => Promise<void>;
  refreshDms: () => Promise<void>;
}

const ChatContext = createContext<ChatContextType | null>(null);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();

  const [groups, setGroups] = useState<Group[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [directConversations, setDirectConversations] = useState<DirectConversation[]>([]);
  const [activeChat, setActiveChat] = useState<ActiveChat>(null);
  const [messages, setMessages] = useState<(Message | DirectMessage)[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  const [activeThread, setActiveThread] = useState<Message | null>(null);
  const [threadReplies, setThreadReplies] = useState<Message[]>([]);

  // Load groups, channels, DMs for current workspace
  const fetchNavData = useCallback(async () => {
    if (!currentWorkspace) {
      setGroups([]);
      setChannels([]);
      setDirectConversations([]);
      setActiveChat(null);
      return;
    }

    try {
      const [groupsRes, channelsRes, dmsRes] = await Promise.all([
        api.groups.list(currentWorkspace.id),
        api.channels.listForWorkspace(currentWorkspace.id),
        api.directConversations.list(currentWorkspace.id)
      ]);

      setGroups(groupsRes);
      setChannels(channelsRes);
      setDirectConversations(dmsRes);

      const firstChannel = channelsRes[0];
      if (firstChannel && !activeChat) {
        setActiveChat({ type: 'channel', channel: firstChannel });
      }
    } catch (err) {
      console.error('Failed to load workspace nav data', err);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    fetchNavData();
  }, [fetchNavData]);

  // Load messages whenever activeChat changes
  const fetchMessages = useCallback(async () => {
    if (!activeChat) {
      setMessages([]);
      return;
    }

    try {
      setIsLoadingMessages(true);
      if (activeChat.type === 'channel') {
        const res = await api.messages.list(activeChat.channel.id, { limit: 50 });
        setMessages([...res.data].reverse());
        setNextCursor(res.nextCursor || null);
        setHasMoreMessages(res.hasMore);
      } else {
        const res = await api.directConversations.listMessages(activeChat.conversation.id, { limit: 50 });
        setMessages([...res.data].reverse());
        setNextCursor(res.nextCursor || null);
        setHasMoreMessages(res.hasMore);
      }
    } catch (err) {
      console.error('Failed to fetch messages', err);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [activeChat]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Realtime STOMP Subscription
  useEffect(() => {
    if (!currentWorkspace || !activeChat) return;

    ws.connect();

    let subTopic = '';
    if (activeChat.type === 'channel') {
      subTopic = topics.channel(currentWorkspace.id, activeChat.channel.id);
    } else {
      subTopic = topics.dm(currentWorkspace.id, activeChat.conversation.id);
    }

    const unsubscribe = ws.subscribe(subTopic, (event: WebSocketEvent) => {
      const payload = event.payload as any;
      if (event.eventType === 'MESSAGE_CREATED' || event.eventType === 'DM_MESSAGE_CREATED') {
        setMessages(prev => {
          if (prev.some(m => m.id === payload.id || (m.clientMessageId && m.clientMessageId === payload.clientMessageId))) {
            return prev.map(m => (m.clientMessageId === payload.clientMessageId ? payload : m));
          }
          return [...prev, payload];
        });
      } else if (event.eventType === 'MESSAGE_UPDATED' || event.eventType === 'DM_MESSAGE_UPDATED') {
        setMessages(prev => prev.map(m => m.id === payload.id ? { ...m, ...payload } : m));
      } else if (event.eventType === 'MESSAGE_REACTION_ADDED' || event.eventType === 'MESSAGE_REACTION_REMOVED' || event.eventType === 'DM_REACTION_ADDED' || event.eventType === 'DM_REACTION_REMOVED') {
        const reactionsWithHasReacted = (payload.reactions || []).map((r: any) => ({
          ...r,
          hasReacted: user?.id ? (r.userIds || []).includes(user.id) : r.hasReacted,
        }));
        setMessages(prev => prev.map(m => m.id === payload.id ? { ...m, reactions: reactionsWithHasReacted } : m));
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentWorkspace, activeChat]);

  const loadOlderMessages = async () => {
    if (!activeChat || !nextCursor || !hasMoreMessages) return;

    try {
      if (activeChat.type === 'channel') {
        const res = await api.messages.list(activeChat.channel.id, { cursor: nextCursor, limit: 50 });
        const older = [...res.data].reverse();
        setMessages(prev => [...older, ...prev]);
        setNextCursor(res.nextCursor || null);
        setHasMoreMessages(res.hasMore);
      } else {
        const res = await api.directConversations.listMessages(activeChat.conversation.id, { cursor: nextCursor, limit: 50 });
        const older = [...res.data].reverse();
        setMessages(prev => [...older, ...prev]);
        setNextCursor(res.nextCursor || null);
        setHasMoreMessages(res.hasMore);
      }
    } catch (err) {
      console.error('Failed to load older messages', err);
    }
  };

  const selectChannel = (channel: Channel) => {
    setActiveChat({ type: 'channel', channel });
    setActiveThread(null);
  };

  const selectDm = (conversation: DirectConversation) => {
    setActiveChat({ type: 'dm', conversation });
    setActiveThread(null);
    api.directConversations.markRead(conversation.id).catch(() => {});
    setDirectConversations(prev => prev.map(d => d.id === conversation.id ? { ...d, unreadCount: 0 } : d));
  };

  const openThread = async (message: Message) => {
    setActiveThread(message);
    try {
      const replies = await api.messages.listReplies(message.id);
      setThreadReplies(replies);
    } catch (err) {
      console.error('Failed to load thread replies', err);
    }
  };

  const closeThread = () => {
    setActiveThread(null);
    setThreadReplies([]);
  };

  const sendMessage = async (content: string) => {
    if (!activeChat || !user) return;
    const clientMessageId = generateClientMessageId();

    if (activeChat.type === 'channel') {
      const created = await api.messages.send(activeChat.channel.id, {
        content,
        clientMessageId
      });
      setMessages(prev => {
        if (prev.some(m => m.id === created.id)) return prev;
        return [...prev, created];
      });
    } else {
      const created = await api.directConversations.sendMessage(activeChat.conversation.id, {
        content,
        clientMessageId
      });
      setMessages(prev => {
        if (prev.some(m => m.id === created.id)) return prev;
        return [...prev, created];
      });
    }
  };

  const sendReply = async (content: string) => {
    if (!activeThread || !user) return;
    const clientMessageId = generateClientMessageId();
    const created = await api.messages.reply(activeThread.id, {
      content,
      clientMessageId,
      parentId: activeThread.id
    });
    setThreadReplies(prev => [...prev, created]);
    setMessages(prev => prev.map(m => m.id === activeThread.id ? { ...m, replyCount: ((m as Message).replyCount || 0) + 1 } : m));
  };

  const editMessage = async (messageId: string, newContent: string) => {
    if (!activeChat) return;
    if (activeChat.type === 'channel') {
      const updated = await api.messages.update(messageId, { content: newContent });
      setMessages(prev => prev.map(m => m.id === messageId ? updated : m));
    } else {
      const updated = await api.directConversations.updateMessage(messageId, { content: newContent });
      setMessages(prev => prev.map(m => m.id === messageId ? updated : m));
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!activeChat) return;
    if (activeChat.type === 'channel') {
      await api.messages.delete(messageId);
    } else {
      await api.directConversations.deleteMessage(messageId);
    }
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, deletedAt: new Date().toISOString(), content: '' } : m));
  };

  const addReaction = async (messageId: string, emoji: string) => {
    if (!activeChat || !user) return;

    // Optimistic local state update
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId) return msg;
        const existingReactions = (msg as Message).reactions || [];
        const found = existingReactions.find((r) => r.emoji === emoji);
        let updatedReactions;
        if (found) {
          if (found.hasReacted) return msg;
          updatedReactions = existingReactions.map((r) =>
            r.emoji === emoji
              ? { ...r, count: r.count + 1, hasReacted: true, userIds: [...(r.userIds || []), user.id] }
              : r
          );
        } else {
          updatedReactions = [
            ...existingReactions,
            { emoji, count: 1, hasReacted: true, userIds: [user.id] },
          ];
        }
        return { ...msg, reactions: updatedReactions };
      })
    );

    try {
      if (activeChat.type === 'channel') {
        await api.messages.addReaction(messageId, emoji);
      } else {
        await api.directConversations.addReaction(messageId, emoji);
      }
    } catch (err) {
      console.error('Failed to add reaction', err);
      await fetchMessages();
    }
  };

  const removeReaction = async (messageId: string, emoji: string) => {
    if (!activeChat || !user) return;

    // Optimistic local state update
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId) return msg;
        const existingReactions = (msg as Message).reactions || [];
        const found = existingReactions.find((r) => r.emoji === emoji);
        if (!found || !found.hasReacted) return msg;

        let updatedReactions;
        if (found.count <= 1) {
          updatedReactions = existingReactions.filter((r) => r.emoji !== emoji);
        } else {
          updatedReactions = existingReactions.map((r) =>
            r.emoji === emoji
              ? {
                  ...r,
                  count: Math.max(0, r.count - 1),
                  hasReacted: false,
                  userIds: (r.userIds || []).filter((id) => id !== user.id),
                }
              : r
          );
        }
        return { ...msg, reactions: updatedReactions };
      })
    );

    try {
      if (activeChat.type === 'channel') {
        await api.messages.removeReaction(messageId, emoji);
      } else {
        await api.directConversations.removeReaction(messageId, emoji);
      }
    } catch (err) {
      console.error('Failed to remove reaction', err);
      await fetchMessages();
    }
  };

  const createChannel = async (groupId: string, name: string, description?: string, isPrivate?: boolean) => {
    const payload = description
      ? { name, description, isPrivate: !!isPrivate, type: 'TEXT' as const }
      : { name, isPrivate: !!isPrivate, type: 'TEXT' as const };
    const created = await api.channels.create(groupId, payload);
    setChannels(prev => [...prev, created]);
    selectChannel(created);
    return created;
  };

  const createGroup = async (name: string, description?: string) => {
    if (!currentWorkspace) throw new Error('No workspace selected');
    const payload = description ? { name, description } : { name };
    const created = await api.groups.create(currentWorkspace.id, payload);
    setGroups(prev => [...prev, created]);
    return created;
  };

  const deleteChannel = async (channelId: string) => {
    await api.channels.delete(channelId);
    setChannels(prev => {
      const remaining = prev.filter(c => c.id !== channelId);
      if (activeChat?.type === 'channel' && activeChat.channel.id === channelId) {
        const nextChannel = remaining[0] || null;
        if (nextChannel) {
          selectChannel(nextChannel);
        } else {
          setActiveChat(null);
        }
      }
      return remaining;
    });
  };

  const deleteGroup = async (groupId: string) => {
    await api.groups.delete(groupId);
    setGroups(prev => prev.filter(g => g.id !== groupId));
    setChannels(prev => {
      const remaining = prev.filter(c => c.groupId !== groupId);
      if (activeChat?.type === 'channel' && activeChat.channel.groupId === groupId) {
        const nextChannel = remaining[0] || null;
        if (nextChannel) {
          selectChannel(nextChannel);
        } else {
          setActiveChat(null);
        }
      }
      return remaining;
    });
  };

  const createDm = async (participantOrReq: string | { participantId?: string; participantIds?: string[]; title?: string }) => {
    if (!currentWorkspace) throw new Error('No workspace selected');
    const payload = typeof participantOrReq === 'string' ? { participantId: participantOrReq } : participantOrReq;
    const created = await api.directConversations.create(currentWorkspace.id, payload);
    setDirectConversations(prev => {
      if (prev.some(d => d.id === created.id)) return prev;
      return [...prev, created];
    });
    selectDm(created);
    return created;
  };

  return (
    <ChatContext.Provider
      value={{
        groups,
        channels,
        directConversations,
        activeChat,
        messages,
        activeThread,
        threadReplies,
        isLoadingMessages,
        hasMoreMessages,
        selectChannel,
        selectDm,
        openThread,
        closeThread,
        sendMessage,
        sendReply,
        editMessage,
        deleteMessage,
        addReaction,
        removeReaction,
        createChannel,
        createGroup,
        deleteChannel,
        deleteGroup,
        createDm,
        loadOlderMessages,
        refreshChannels: fetchNavData,
        refreshDms: fetchNavData
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used within a ChatProvider');
  return context;
};
